import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { addInteractionEvent } from '@creditornot/browser-telemetry';
import { Modal } from '@creditornot/cbt-modal';
import { Slide, UserGuideContext } from 'common/api-types/director';
import { PageApiError } from 'common/components/cookbook/PageApiError';
import { PageSpinner } from 'common/components/cookbook/PageSpinner';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useKeydown } from 'common/hooks/useKeydown';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { usePageView } from 'common/hooks/usePageView';

import { TIME_PER_STEP } from './constants';
import { Header } from './Header';
import { useUserGuidesQuery } from './query';
import { Slides } from './Slides';

type BackgroundType = Slide['background'];

const ModalContentContainer = styled.div<{ $background: BackgroundType }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ $background }) => ($background === 'PRIMARY' ? '#00c2e8' : '#0F2594')};
`;

const useAutomaticNextStepChange = (
  currentStep: number,
  isPaused: boolean,
  progressBeforePause: number,
  isOnLastStep: boolean,
  handleNextStepChange: (type: 'click' | 'automatic') => void,
) => {
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const timeUntilStepChange = TIME_PER_STEP - progressBeforePause;
    intervalRef.current = setInterval(() => {
      handleNextStepChange('automatic');
    }, timeUntilStepChange);

    if (isPaused) {
      clearInterval(intervalRef.current);
    }
    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isPaused, progressBeforePause, isOnLastStep, currentStep]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return intervalRef.current;
};

type UserGuidesProps = {
  isVisible: boolean;
  context: UserGuideContext;
  selectedWarehouseId?: string;
  onClose: () => void;
  children: React.ReactElement;
};

type UserGuidesContentProps = Omit<UserGuidesProps, 'isVisible' | 'children'> & {
  currentStep: number;
  onStepChange: React.Dispatch<React.SetStateAction<number>>;
};

const UserGuidesContent = ({
  context,
  currentStep,
  onClose,
  onStepChange,
  selectedWarehouseId,
}: UserGuidesContentProps) => {
  const formatMessage = useFormatMessage();

  const [{ isPaused, progressBeforePause }, setPausedState] = useState({
    isPaused: false,
    progressBeforePause: 0,
    pausedAt: Date.now(),
  });

  const { data, isLoading, error } = useUserGuidesQuery({
    venueId: selectedWarehouseId,
    context,
  });

  const userGuide = data?.[0];
  const topic = userGuide?.topic;
  const slides = userGuide?.slides;
  const totalSteps = slides?.length ?? 0;
  const isOnLastStep = currentStep === totalSteps - 1;

  const background = useMemo(
    () => slides?.[currentStep]?.background ?? 'PRIMARY',
    [currentStep, slides],
  );

  usePageView(
    'user_guides',
    {
      wm_venue_id: selectedWarehouseId,
      content_topic: topic,
      slide_count: totalSteps,
    },
    { disabled: !topic },
  );

  const initializePauseAction = () => {
    setPausedState({
      isPaused: false,
      progressBeforePause: 0,
      pausedAt: Date.now(),
    });
  };

  const handlePauseAction = () => {
    const now = Date.now();
    setPausedState((prev) => {
      addInteractionEvent('user_guides', 'click', {
        wm_venue_id: selectedWarehouseId,
        click_target: isPaused ? 'resume' : 'pause',
        slide_rank: currentStep,
        slide_count: totalSteps,
        content_topic: topic,
      });

      return {
        isPaused: !prev.isPaused,
        progressBeforePause: prev.isPaused
          ? prev.progressBeforePause
          : prev.progressBeforePause + (now - prev.pausedAt),
        pausedAt: now,
      };
    });
  };

  const handleNextStepChange = useCallback(
    (type: 'click' | 'automatic') => {
      onStepChange((prevStep) => {
        if (type === 'click') {
          addInteractionEvent('user_guides', 'click', {
            wm_venue_id: selectedWarehouseId,
            click_target: 'next',
            slide_rank: prevStep + 1,
            slide_count: totalSteps,
            content_topic: topic,
          });
        } else {
          addInteractionEvent('user_guides', 'automatic_slide_change', {
            wm_venue_id: selectedWarehouseId,
            slide_rank: prevStep + 1,
            slide_count: totalSteps,
            content_topic: topic,
          });
        }

        return prevStep + 1 === totalSteps ? prevStep : prevStep + 1;
      });
      initializePauseAction();
    },
    [selectedWarehouseId, topic, totalSteps, onStepChange],
  );

  const handlePreviousStepChange = () => {
    onStepChange((prevStep) => {
      addInteractionEvent('user_guides', 'click', {
        wm_venue_id: selectedWarehouseId,
        click_target: 'previous',
        slide_rank: prevStep + 1,
        slide_count: totalSteps,
        content_topic: topic,
      });

      return prevStep === 0 ? prevStep : prevStep - 1;
    });
    initializePauseAction();
  };

  const handleStepClick = (step: number) => {
    onStepChange(step);
  };

  useKeydown((event) => {
    if (event.code === 'Space') {
      handlePauseAction();
    }

    if (event.code === 'ArrowRight') {
      handleNextStepChange('click');
    }

    if (event.code === 'ArrowLeft') {
      handlePreviousStepChange();
    }
  });

  const automaticNavigationIntervalId = useAutomaticNextStepChange(
    currentStep,
    isPaused,
    progressBeforePause,
    isOnLastStep,
    handleNextStepChange,
  );

  return (
    <ModalContentContainer $background={background}>
      <Header
        title={userGuide?.title?.value ?? formatMessage('user_guides.user_guide')}
        currentStep={currentStep}
        totalSteps={totalSteps}
        isPaused={isPaused}
        progressBeforePause={progressBeforePause}
        onStepClick={handleStepClick}
        onClose={onClose}
      />
      {isLoading ? (
        <PageSpinner />
      ) : error ? (
        <PageApiError error={error} />
      ) : userGuide ? (
        <Slides
          slides={userGuide.slides}
          currentStep={currentStep}
          totalSteps={totalSteps}
          isPaused={isPaused}
          onNextStep={() => {
            clearInterval(automaticNavigationIntervalId);
            handleNextStepChange('click');
          }}
          onPreviousStep={() => {
            clearInterval(automaticNavigationIntervalId);
            handlePreviousStepChange();
          }}
          onPauseAction={handlePauseAction}
        />
      ) : null}
    </ModalContentContainer>
  );
};

export const UserGuides = ({
  context,
  selectedWarehouseId,
  isVisible,
  children,
  onClose,
  ...rest
}: UserGuidesProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  const maxLg = useMatchMedia('maxLg');

  const { data: userGuides } = useUserGuidesQuery(
    {
      venueId: selectedWarehouseId,
      context,
    },
    { enabled: isVisible },
  );

  const userGuide = userGuides?.[0];

  const handleModalClose = () => {
    addInteractionEvent('user_guides', 'click', {
      wm_venue_id: selectedWarehouseId,
      click_target: 'close',
      current_rank: currentStep,
      slide_count: userGuide?.slides.length,
      content_topic: userGuide?.topic,
    });
    onClose();
  };

  return (
    <>
      {children}
      <Modal
        disableContentPadding
        open={isVisible}
        sizePreset={maxLg ? 'fullScreen' : 'centered:medium'}
        onDismiss={handleModalClose}
        onUnmount={() => setCurrentStep(0)}
      >
        <UserGuidesContent
          context={context}
          currentStep={currentStep}
          selectedWarehouseId={selectedWarehouseId}
          onClose={handleModalClose}
          onStepChange={setCurrentStep}
          {...rest}
        />
      </Modal>
    </>
  );
};
